import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Sparkles, ArrowLeft, Loader2, Coins, Zap, Crown, RefreshCw, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const PLAN_ICON = { free: Coins, basico: Zap, creador: Crown, estudio: Sparkles };

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(null);

  // Catálogo público: sin cookies (evita bloqueos CORS/sesión), con timeout y 3 intentos antes de mostrar error.
  const loadCatalog = async () => {
    setLoadFailed(false);
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const r = await api.get("/pricing", { withCredentials: false, timeout: 8000 });
        if (r?.data?.plans) { setCatalog(r.data); return; }
      } catch { /* reintenta */ }
      await new Promise((res) => setTimeout(res, 1200 * (attempt + 1)));
    }
    setLoadFailed(true);
  };

  useEffect(() => { loadCatalog(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const goLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const buy = async (lookup) => {
    if (!lookup) { user ? navigate("/dashboard") : goLogin(); return; }
    if (!user) { toast.info("Inicia sesión para comprar créditos"); setTimeout(goLogin, 700); return; }
    setBusy(lookup);
    try {
      const r = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el pago");
      setBusy(null);
    }
  };

  if (!catalog) {
    return (
      <div className="min-h-screen flex items-center justify-center grain px-6">
        {loadFailed ? (
          <div className="mfp-card p-8 max-w-sm text-center space-y-4" data-testid="pricing-error">
            <AlertTriangle className="w-8 h-8 mx-auto text-[#FF9F2E]" />
            <p className="font-head text-lg">No pudimos cargar los precios.</p>
            <p className="text-sm text-[#94A3B8]">Revisa tu conexión y vuelve a intentarlo.</p>
            <button data-testid="pricing-retry-btn" onClick={loadCatalog}
              className="inline-flex items-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold px-5 py-2.5 rounded-full transition-colors">
              <RefreshCw className="w-4 h-4" /> Reintentar
            </button>
          </div>
        ) : (
          <Loader2 className="w-7 h-7 text-[#FF334B] spin" data-testid="pricing-loading" />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen grain relative overflow-hidden pb-24" data-testid="pricing-page">
      <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(255,51,75,0.22), transparent 70%)" }} />
      <div className="pointer-events-none absolute -bottom-56 -left-40 w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(0,240,255,0.12), transparent 70%)" }} />

      <header className="relative z-10 flex items-center justify-between px-5 sm:px-10 py-6">
        <Logo />
        <button data-testid="pricing-back-btn" onClick={() => navigate(user ? "/dashboard" : "/")}
          className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border border-white/15 hover:border-[#FF334B] hover:text-[#FF334B] transition-colors">
          <ArrowLeft className="w-4 h-4" /> {user ? "Al estudio" : "Inicio"}
        </button>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-5 sm:px-10 pt-4">
        <div className="text-center mb-12 fade-up">
          <div className="mono-label mb-4">Precios · 1 crédito ≈ ${catalog.credit_usd.toFixed(2)}</div>
          <h1 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight">
            Elige tu <span className="text-[#FF334B]">plan</span>
          </h1>
          <p className="mt-4 text-base text-[#94A3B8] max-w-2xl mx-auto">
            Los créditos alimentan cada canción, video y cortometraje. Suscríbete para recibir créditos
            cada mes, o compra un pack cuando lo necesites.
          </p>
        </div>

        {/* Planes mensuales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {catalog.plans.map((p) => {
            const Icon = PLAN_ICON[p.id] || Coins;
            const isCurrent = user && p.id === "free"; // el plan real se muestra en Créditos
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`}
                className={`relative mfp-card p-6 flex flex-col ${p.popular ? "border-[#FF334B] ring-1 ring-[#FF334B]/40" : ""}`}>
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[0.65rem] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-[#FF334B] text-white">
                    Más popular
                  </span>
                )}
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${p.popular ? "bg-[#FF334B]/15 text-[#FF334B]" : "bg-white/8 text-[#FF9F2E]"}`}>
                    <Icon className="w-4.5 h-4.5" />
                  </div>
                  <span className="font-head font-bold text-lg">{p.name}</span>
                </div>
                <div className="mb-1">
                  <span className="font-head text-3xl font-extrabold">${p.price.toFixed(2)}</span>
                  {p.price > 0 && <span className="text-sm text-[#64748B]">/mes</span>}
                </div>
                <div className="text-sm text-[#00F0FF] font-semibold mb-4">{p.credits} créditos{p.price > 0 ? " / mes" : ""}</div>
                <ul className="space-y-2 mb-6 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#94A3B8]">
                      <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <button data-testid={`plan-buy-${p.id}`} onClick={() => buy(p.lookup)} disabled={busy === p.lookup || isCurrent}
                  className={`w-full inline-flex items-center justify-center gap-2 font-semibold px-4 py-3 rounded-full transition-colors disabled:opacity-60 ${
                    p.popular ? "bg-[#FF334B] hover:bg-[#E0223A] text-white" : p.id === "free" ? "border border-white/15 hover:border-white/30 text-white" : "bg-white/10 hover:bg-white/15 text-white"}`}>
                  {busy === p.lookup ? <Loader2 className="w-4 h-4 spin" /> : null}
                  {p.id === "free" ? (user ? "Tu plan base" : "Empezar gratis") : "Suscribirme"}
                </button>
              </div>
            );
          })}
        </div>

        {/* Packs de un solo uso */}
        <div className="mb-16">
          <div className="text-center mb-6">
            <h2 className="font-head text-2xl font-bold">Packs de créditos</h2>
            <p className="text-sm text-[#94A3B8] mt-1">Pago único · nunca expiran · se suman a tu saldo.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {catalog.packs.map((p) => (
              <div key={p.id} data-testid={`pack-card-${p.id}`} className="mfp-card p-6 text-center flex flex-col">
                <Coins className="w-7 h-7 text-[#FF9F2E] mx-auto mb-3" />
                <div className="font-head text-2xl font-extrabold text-[#00F0FF]">{p.credits}</div>
                <div className="text-xs text-[#64748B] mb-3">créditos</div>
                <div className="font-head text-xl font-bold mb-4">${p.price.toFixed(2)}</div>
                <button data-testid={`pack-buy-${p.id}`} onClick={() => buy(p.lookup)} disabled={busy === p.lookup}
                  className="mt-auto w-full inline-flex items-center justify-center gap-2 font-semibold px-4 py-2.5 rounded-full bg-white/10 hover:bg-white/15 transition-colors disabled:opacity-60">
                  {busy === p.lookup ? <Loader2 className="w-4 h-4 spin" /> : null} Comprar
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Tabla de costos por acción */}
        <div className="max-w-2xl mx-auto mfp-card p-6" data-testid="pricing-actions-table">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-[#FF9F2E]" />
            <h2 className="font-head text-xl font-bold">¿Cuánto cuesta cada acción?</h2>
          </div>
          <div className="divide-y divide-white/8">
            {catalog.actions.map((a, i) => (
              <div key={i} className="flex items-center justify-between py-3">
                <span className="text-sm text-[#94A3B8]">{a.label}</span>
                <span className="inline-flex items-center gap-1.5 font-head font-bold text-white">
                  <Coins className="w-4 h-4 text-[#FF9F2E]" /> {a.credits} <span className="text-xs text-[#64748B] font-normal">cr</span>
                </span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#64748B] mt-4">
            Si una generación falla por un problema técnico, tus créditos se reembolsan automáticamente.
          </p>
        </div>
      </main>
    </div>
  );
}
